import React, { useState, useRef, useEffect } from 'react';
import { FileText, Video, ExternalLink, Maximize2, Minimize2, Move } from 'lucide-react';

const SupportLinks = ({ docUrl, videoUrl }) => {
    const [modalContent, setModalContent] = useState(null);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 800, height: 600 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isMaximized, setIsMaximized] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const modalRef = useRef(null);
    const previousSize = useRef({ width: 800, height: 600 });

    if (!docUrl && !videoUrl) return null;

    const getYouTubeEmbedUrl = (url) => {
        try {
            const videoId = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&\s]+)/);
            return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url;
        } catch {
            return url;
        }
    };

    const openInModal = (url, type) => {
        setLoadError(false);
        setModalPosition({ x: window.innerWidth / 2 - 400, y: window.innerHeight / 2 - 300 });
        setModalSize({ width: 800, height: 600 });
        setIsMaximized(false);

        // Handle different content types
        if (type === 'video') {
            if (url.includes('youtube.com') || url.includes('youtu.be')) {
                setModalContent({
                    type: 'youtube',
                    url: getYouTubeEmbedUrl(url),
                    originalUrl: url
                });
            } else {
                setModalContent({
                    type: 'video',
                    url: url
                });
            }
        } else {
            setModalContent({
                type: 'document',
                url: url
            });
        }
    };

    const openInNewWindow = () => {
        if (modalContent) {
            window.open(modalContent.originalUrl || modalContent.url, '_blank');
        }
    };

    const handleMouseDown = (e) => {
        if (e.target.closest('.modal-controls')) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - modalPosition.x,
            y: e.clientY - modalPosition.y
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newX = Math.min(
            Math.max(0, e.clientX - dragStart.x), 
            window.innerWidth - modalSize.width
        );
        const newY = Math.min(
            Math.max(0, e.clientY - dragStart.y),
            window.innerHeight - modalSize.height
        );

        setModalPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const toggleMaximize = () => {
        if (isMaximized) {
            setModalSize(previousSize.current);
            setModalPosition({ 
                x: window.innerWidth / 2 - previousSize.current.width / 2,
                y: window.innerHeight / 2 - previousSize.current.height / 2
            });
        } else {
            previousSize.current = modalSize;
            setModalSize({ width: window.innerWidth, height: window.innerHeight });
            setModalPosition({ x: 0, y: 0 });
        }
        setIsMaximized(!isMaximized);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);

    const renderModalContent = () => {
        if (loadError) {
            return (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8">
                    <div className="text-red-600 mb-4">Failed to load content</div>
                    <button
                        onClick={openInNewWindow}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Open in New Window
                    </button>
                </div>
            );
        }

        if (!modalContent) return null;

        if (modalContent.type === 'youtube') {
            return (
                <div className="w-full h-full bg-black flex items-center justify-center">
                    <iframe
                        src={modalContent.url}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        onError={() => setLoadError(true)}
                    />
                </div>
            );
        }

        if (modalContent.type === 'video') {
            return (
                <div className="w-full h-full bg-black flex items-center justify-center">
                    <video
                        controls
                        className="w-full h-full"
                        onError={() => setLoadError(true)}
                    >
                        <source src={modalContent.url} />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }

        return (
            <iframe
                src={modalContent.url}
                className="w-full h-full"
                onError={() => setLoadError(true)}
            />
        );
    };

    return (
        <>
            <div className="flex gap-4 mt-4">
                {docUrl && (
                    <button
                        onClick={() => openInModal(docUrl, 'document')}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                        <FileText className="w-4 h-4" />
                        Documentation
                    </button>
                )}
                {videoUrl && (
                    <button
                        onClick={() => openInModal(videoUrl, 'video')}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                        <Video className="w-4 h-4" />
                        Watch Video
                    </button>
                )}
            </div>

            {/* Resizable/Draggable Modal */}
            {modalContent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
                    <div
                        ref={modalRef}
                        className="absolute bg-white rounded-lg shadow-xl overflow-hidden"
                        style={{
                            width: `${modalSize.width}px`,
                            height: `${modalSize.height}px`,
                            left: `${modalPosition.x}px`,
                            top: `${modalPosition.y}px`,
                            cursor: isDragging ? 'grabbing' : 'grab'
                        }}
                    >
                        {/* Modal Header - Draggable Area */}
                        <div
                            className="flex justify-between items-center p-4 border-b bg-gray-50"
                            onMouseDown={handleMouseDown}
                        >
                            <div className="flex items-center gap-2">
                                <Move className="w-4 h-4 text-gray-400" />
                                <h3 className="text-lg font-semibold">
                                    {modalContent.type === 'youtube' || modalContent.type === 'video' 
                                        ? 'Video' 
                                        : 'Documentation'
                                    }
                                </h3>
                            </div>
                            
                            <div className="flex items-center gap-2 modal-controls">
                                <button
                                    onClick={openInNewWindow}
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                    title="Open in new window"
                                >
                                    <ExternalLink className="w-5 h-5 text-gray-600" />
                                </button>
                                <button
                                    onClick={toggleMaximize}
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                    title={isMaximized ? "Restore" : "Maximize"}
                                >
                                    {isMaximized 
                                        ? <Minimize2 className="w-5 h-5 text-gray-600" />
                                        : <Maximize2 className="w-5 h-5 text-gray-600" />
                                    }
                                </button>
                                <button
                                    onClick={() => setModalContent(null)}
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="w-full h-[calc(100%-64px)]">
                            {renderModalContent()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportLinks;